import React, { useState, useEffect, useContext } from "react";
import { Box, Group } from "@mantine/core";
import { ViewsMenu } from "./filters/ViewsMenu";
import IdeasTab from "./IdeasTab";
import { useLocation } from "react-router-dom";
import FiltersContext from "./filters/context/FiltersContext";
import { isBagelAiTab } from "../../utils/viewUtils";
import { TEST_IDS } from "./constants";

const TAB_GAP = 4;
const TAB_WIDTH = 80 + TAB_GAP;

const IdeasTabs = ({
  savedViews,
  selectedView,
  setSelectedView,
  upsertViews,
  containerRef,
  isArchived, 
  isGlobal,
  setIsGlobal,
  isSubPageView,
  setIsSubPageView,
}: {
  savedViews: any;
  selectedView: any;
  setSelectedView: (value: any) => void;
  upsertViews: (body: any, func: (res: any) => void) => void;
  SplitOn: number;
  containerRef: React.MutableRefObject<null>; 
  isArchived: boolean;  
  isGlobal: boolean;
  setIsGlobal: (isGlobal: boolean) => void;
  isSubPageView: boolean;
  setIsSubPageView: (isSubPageView: boolean) => void;
}) => {
  const [splitOn, setSplitOn] = useState(savedViews.length);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const _selectedView = queryParams.get("selectedView");

  const [bagelAiView, setBagelAiView] = useState<any>({});
  const [views, setViews] = useState([]);
   
  const { setActiveFilters, activeFilters, subPageKey } = useContext(FiltersContext);
  
  useEffect(() => {
    const filteredViews = savedViews.filter(
      (savedView: any) => !isBagelAiTab(savedView)
    );
    setViews(filteredViews);
    const bagelAi = savedViews.find(
      (savedView: any) => isBagelAiTab(savedView)
    );
    setBagelAiView(bagelAi);
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const containerWidth = entry.contentRect.width;
        const maxTabs = Math.floor(containerWidth / TAB_WIDTH);
        setSplitOn(Math.max(1, Math.min(maxTabs, savedViews.length)));
      }
    });
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [savedViews]);

  return (
    <Box mt={6}>
      <Group spacing={4}>
        {views.slice(0, splitOn).map((view: any) => (
          <IdeasTab
            key={view._id}
            view={view}
            isActive={view.name === _selectedView}
            onTabChange={(item_id) => {
              const newView = savedViews.find(
                (view: any) => view._id === item_id
              );
              if (newView.name !== _selectedView) {
                // Adjust active filters by removing previous view selections and adding new view selections
                setActiveFilters((prev: any[] = []) => {
                  const prevViewFilters = selectedView?.filters || [];
                  const newViewFilters = newView?.filters || [];

                  const normalizeKey = (f: any) => (f?.name || f?.parent || '').toLowerCase();
                  const toLowerArray = (arr: any[]) =>
                    (Array.isArray(arr) ? arr : []).map((v) => (typeof v === 'string' ? v.toLowerCase() : v));

                  // Create lookup maps
                  const prevMap = new Map<string, string[]>();
                  prevViewFilters.forEach((f: any) => {
                    prevMap.set(normalizeKey(f), toLowerArray(f.selected || []));
                  });

                  const newMap = new Map<string, { selected: string[]; filter: any }>();
                  newViewFilters.forEach((f: any) => {
                    newMap.set(normalizeKey(f), { selected: toLowerArray(f.selected || []), filter: f });
                  });

                  // Start from current active filters and mutate selected arrays
                  let next = (prev || []).map((af: any) => {
                    const key = normalizeKey(af);
                    const prevVals = prevMap.get(key) || [];
                    const addVals = newMap.get(key)?.selected || [];

                    const currentLower = toLowerArray(af.selected || []);
                    // Remove values contributed by previous view
                    const withoutPrev = currentLower.filter((val) => !prevVals.includes(val));
                    // Add values from new view
                    const merged = Array.from(new Set([...withoutPrev, ...addVals]));

                    return {
                      ...af,
                      selected: merged,
                    };
                  });

                  // Remove filters that ended up with empty selections
                  next = next.filter((f: any) => !Array.isArray(f.selected) || f.selected.length > 0);

                  // Add filters that are in the new view but not present in active filters
                  const existingKeys = new Set(next.map((f: any) => normalizeKey(f)));
                  newViewFilters.forEach((f: any) => {
                    const key = normalizeKey(f);
                    if (!existingKeys.has(key)) {
                      const selected = toLowerArray(f.selected || []);
                      if (selected.length > 0) {
                        next = [
                          {
                            ...f,
                            selected,
                          },
                          ...next,
                        ];
                      }
                    }
                  });

                  return next;
                });
              }
              setSelectedView(newView);
            }}
            upsertViews={upsertViews}
            isGenerated={false}
            isGlobal={isGlobal}
            setIsGlobal={setIsGlobal}
            isSubPageView={isSubPageView}
            setIsSubPageView={setIsSubPageView}
            activeFilters={activeFilters}
            subPageKey={subPageKey}
          />
        ))}
        {savedViews.length > splitOn && (
          <ViewsMenu
            savedViews={savedViews.slice(splitOn)}
            selectedView={savedViews.find(
              (sv: any) => sv?.name?.toLowerCase() == _selectedView?.toLowerCase()
            )}
            setSelectedView={setSelectedView}
            upsertViews={upsertViews}
            activeFilters={activeFilters}
          />
        )}
        {bagelAiView && !isArchived && (
          <Box data-testid={TEST_IDS.bagelAiTab}>
            <IdeasTab
              key={bagelAiView._id}
              view={bagelAiView}
              isActive={isBagelAiTab(bagelAiView) && bagelAiView?.name?.toLowerCase() === _selectedView?.toLowerCase()}
              onTabChange={(item_id) => {
                const newView = savedViews.find(
                  (view: any) => view._id === item_id
                );
                if (newView.name !== _selectedView) {
                  // Adjust active filters by removing previous view selections and adding new view selections
                  setActiveFilters((prev: any[] = []) => {
                    const prevViewFilters = selectedView?.filters || [];
                    const newViewFilters = newView?.filters || [];
  
                    const normalizeKey = (f: any) => (f?.name || f?.parent || '').toLowerCase();
                    const toLowerArray = (arr: any[]) =>
                      (Array.isArray(arr) ? arr : []).map((v) => (typeof v === 'string' ? v.toLowerCase() : v));
  
                    // Create lookup maps
                    const prevMap = new Map<string, string[]>();
                    prevViewFilters.forEach((f: any) => {
                      prevMap.set(normalizeKey(f), toLowerArray(f.selected || []));
                    });
  
                    const newMap = new Map<string, { selected: string[]; filter: any }>();
                    newViewFilters.forEach((f: any) => {
                      newMap.set(normalizeKey(f), { selected: toLowerArray(f.selected || []), filter: f });
                    });
  
                    // Start from current active filters and mutate selected arrays
                    let next = (prev || []).map((af: any) => {
                      const key = normalizeKey(af);
                      const prevVals = prevMap.get(key) || [];
                      const addVals = newMap.get(key)?.selected || [];
  
                      const currentLower = toLowerArray(af.selected || []);
                      // Remove values contributed by previous view
                      const withoutPrev = currentLower.filter((val) => !prevVals.includes(val));
                      // Add values from new view
                      const merged = Array.from(new Set([...withoutPrev, ...addVals]));
  
                      return {
                        ...af,
                        selected: merged,
                      };
                    });
  
                    // Remove filters that ended up with empty selections
                    next = next.filter((f: any) => !Array.isArray(f.selected) || f.selected.length > 0);
  
                    // Add filters that are in the new view but not present in active filters
                    const existingKeys = new Set(next.map((f: any) => normalizeKey(f)));
                    newViewFilters.forEach((f: any) => {
                      const key = normalizeKey(f);
                      if (!existingKeys.has(key)) {
                        const selected = toLowerArray(f.selected || []);
                        if (selected.length > 0) {
                          next = [
                            {
                              ...f,
                              selected,
                            },
                            ...next,
                          ];
                        }
                      }
                    });
  
                    return next;
                  });
                }
                setSelectedView(newView);
              }}
              upsertViews={upsertViews}
              isGenerated={true}
              isGlobal={isGlobal}
              setIsGlobal={setIsGlobal}
              isSubPageView={isSubPageView}
              setIsSubPageView={setIsSubPageView}
              activeFilters={activeFilters}
              subPageKey={subPageKey}
            />
          </Box>
        )}
      </Group>
    </Box>
  );
};

export default IdeasTabs;
